import React, { useState, useMemo } from 'react';
import { ServerList } from './ServerList';
import { ServerDetailsPanel } from './ServerDetailsPanel';
import { ServerForm } from './ServerForm';
import { useMCPServers } from './hooks/useMCPServers';
import { useToolExecution } from './hooks/useToolExecution';
import { useServerForm } from './hooks/useServerForm';

export interface MCPServerManagerProps {
  className?: string;
  onClose?: () => void;
}

export const MCPServerManager: React.FC<MCPServerManagerProps> = ({
  className = "",
  onClose,
}) => {
  const mcpServers = useMCPServers();
  const toolExecution = useToolExecution(mcpServers.selectedServer);
  const serverForm = useServerForm();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter tools based on search query
  const filteredCapabilities = useMemo(() => {
    if (!mcpServers.selectedServer?.capabilities) return [];
    if (!searchQuery.trim()) return mcpServers.selectedServer.capabilities;
    
    return mcpServers.selectedServer.capabilities.filter(capability => 
      capability.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      capability.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [mcpServers.selectedServer?.capabilities, searchQuery]);

  const handleAddServer = () => {
    serverForm.handleAddServer();
    mcpServers.handleAddServer();
  };

  const handleEditServer = (server: any) => {
    serverForm.handleEditServer(server);
    mcpServers.handleEditServer(server);
  };

  const handleSaveServer = async () => {
    const result = await mcpServers.handleSaveServer(serverForm.formData, serverForm.isEditing);
    if (result.success) {
      serverForm.resetForm();
    }
  };

  const handleCancelForm = () => {
    serverForm.handleCancel();
    mcpServers.handleSelectServer(mcpServers.selectedServer!);
  };

  const handleExecuteTool = async (toolData: any) => {
    const result = await toolExecution.handleExecuteTool(toolData);
    if (!result.success) {
      // Error handling is already done in the hook
      console.error('Tool execution failed:', result.error);
    }
  };

  return (
    <div className={`fixed inset-0 bg-gray-900 text-white z-50 flex flex-col ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-800">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold">MCP Server Management</h2>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Live</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleAddServer}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors"
          >
            + Add Server
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {mcpServers.error && (
        <div className="p-4 bg-red-600/20 border-b border-red-600/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-300 font-medium">{mcpServers.error}</p>
            </div>
            <button
              onClick={mcpServers.clearError}
              className="text-red-400 hover:text-red-300 text-sm font-medium"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Server List */}
        <ServerList
          servers={mcpServers.servers}
          selectedServer={mcpServers.selectedServer}
          loading={mcpServers.loading}
          onSelectServer={mcpServers.handleSelectServer}
          onToggleServer={mcpServers.handleToggleServer}
          onTestConnection={mcpServers.handleTestConnection}
          onEditServer={handleEditServer}
          onDeleteServer={mcpServers.handleDeleteServer}
        />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="p-6 border-b border-gray-700 bg-gray-800/50">
            <h3 className="text-xl font-semibold text-white">
              {mcpServers.selectedServer ? mcpServers.selectedServer.name : 'Select a Server'}
            </h3>
            {mcpServers.selectedServer && (
              <p className="text-sm text-gray-400 mt-1">{mcpServers.selectedServer.description}</p>
            )}
          </div>
          
          <div className="flex-1 overflow-hidden">
            {serverForm.isAddingServer || serverForm.isEditing ? (
              <ServerForm
                isEditing={serverForm.isEditing}
                formData={serverForm.formData}
                onFormDataChange={serverForm.handleFormDataChange}
                onSave={handleSaveServer}
                onCancel={handleCancelForm}
                selectedServer={mcpServers.selectedServer}
                loading={mcpServers.loading}
                executingTool={toolExecution.executingTool}
                onStartToolExecution={toolExecution.handleStartToolExecution}
                onExecuteTool={handleExecuteTool}
                onCancelToolExecution={toolExecution.handleCancelToolExecution}
                executionResult={toolExecution.getExecutionResult()}
              />
            ) : mcpServers.selectedServer ? (
              <ServerDetailsPanel
                server={mcpServers.selectedServer}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                filteredCapabilities={filteredCapabilities}
                executingTool={toolExecution.executingTool}
                onStartToolExecution={toolExecution.handleStartToolExecution}
                onExecuteTool={handleExecuteTool}
                onCancelToolExecution={toolExecution.handleCancelToolExecution}
                executionResult={toolExecution.getExecutionResult()}
                loading={mcpServers.loading}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">No Server Selected</h3>
                  <p className="text-gray-400 mb-4">Choose a server from the left panel to view details and manage tools</p>
                  <button
                    onClick={handleAddServer}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
                  >
                    Add Your First Server
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
